import { useCallback, useEffect, useState } from "react";
import {
  detachListingCanonical,
  fetchCanonicalVehicle,
  recalculateCanonicalVehicle,
} from "../../../../services/vehicleDuplicateApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";

function formatCop(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const CanonicalVehicleDetailPanel = ({ canonicalId, onBack, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const result = await fetchCanonicalVehicle(canonicalId);
      setData(result);
    } catch (error) {
      setData(null);
      setErrorMessage(error.message || "Unable to load canonical vehicle.");
    } finally {
      setLoading(false);
    }
  }, [canonicalId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalculate = async () => {
    try {
      await recalculateCanonicalVehicle(canonicalId);
      setToast("Summary recalculated.");
      await load();
    } catch (error) {
      setToast(error.message || "Recalculate failed.");
    }
  };

  const handleDetach = async (listingId) => {
    try {
      await detachListingCanonical(listingId);
      setToast("Listing detached.");
      await load();
    } catch (error) {
      setToast(error.message || "Detach failed.");
    }
  };

  const canonical = data?.canonical;
  const listings = data?.listings || [];
  const quality = data?.quality;

  return (
    <div className="admin-dashboard admin-canonical-detail-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">
            {canonical
              ? `${canonical.makeNormalized} ${canonical.modelNormalized} ${canonical.year || ""}`
              : "Canonical vehicle"}
          </h1>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={onBack}>
            Back
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={handleRecalculate}>
            Recalculate
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-dashboard__toast">{toast}</p> : null}

      {loading ? (
        <AdminStatePanel message="Loading…" />
      ) : errorMessage ? (
        <AdminStatePanel message={errorMessage} variant="error" />
      ) : canonical ? (
        <>
          <section className="admin-dashboard__summary-cards">
            <div className="admin-summary-card">
              <span>Lowest price</span>
              <strong>{formatCop(canonical.lowestCurrentPriceCop)}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Median price</span>
              <strong>{formatCop(canonical.medianCurrentPriceCop)}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Highest price</span>
              <strong>{formatCop(canonical.highestCurrentPriceCop)}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Active listings</span>
              <strong>{canonical.activeListingCount}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Sources</span>
              <strong>{canonical.sourceCount}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Status</span>
              <strong>{canonical.canonicalStatus}</strong>
            </div>
          </section>

          {quality ? (
            <section className="admin-dashboard__panel">
              <h2>Data quality</h2>
              <p>Canonical quality status: {quality.canonicalQualityStatus}</p>
              <p>Linked listings: {quality.linkedListingsCount}</p>
              <p>
                Conflicting fields:{" "}
                {quality.conflictingFields?.length
                  ? quality.conflictingFields.join(", ")
                  : "None"}
              </p>
              <p>
                Price spread:{" "}
                {quality.priceSpread != null ? formatCop(quality.priceSpread) : "—"}
              </p>
              <p>
                Mileage spread:{" "}
                {quality.mileageSpread != null ? `${quality.mileageSpread} km` : "—"}
              </p>
              <p>Open listing quality issues: {quality.openListingQualityIssues ?? 0}</p>
              {quality.recommendedActions?.length ? (
                <ul>
                  {quality.recommendedActions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          <h2 className="admin-dashboard__section-title">Linked listings</h2>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Seller</th>
                  <th>Price</th>
                  <th>Mileage</th>
                  <th>Status</th>
                  <th>Last seen</th>
                  <th>URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((l) => (
                  <tr key={l.id}>
                    <td>{l.sourceName || l.sourceKey}</td>
                    <td>{l.sellerName || "—"}</td>
                    <td>{formatCop(l.currentPriceCop ?? l.priceCop)}</td>
                    <td>{l.mileageKm != null ? `${l.mileageKm} km` : "—"}</td>
                    <td>{l.listingStatus}</td>
                    <td>{formatDate(l.lastSeenAt)}</td>
                    <td>
                      {l.sourceUrl ? (
                        <a href={l.sourceUrl} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-dashboard__action-btn"
                        onClick={() => handleDetach(l.id)}
                      >
                        Detach
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </div>
  );
};

export default CanonicalVehicleDetailPanel;
